import { memo, useCallback, useMemo } from 'react';
import { Obstacle } from '@thive/fddm-types';

import { GapHorizontalIcon } from '@/assets/icons';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { MIN_WALL_THICKNESS } from '@/modules/floorplanDesign/constants/obstacle';
import { NumUtils } from '@/modules/common/utils';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';

interface Props {
  selectedWalls: Obstacle[];
}

/** @description Thickness is only editable on walls made of more than one segment. */
const hasEditableThickness = ({ segments }: Obstacle): boolean => segments.length > 1;

function ThicknessComponent({ selectedWalls }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  // Shown when any selected wall has an editable thickness, only editable when all of them do
  const thicknessWalls = useMemo(() => selectedWalls.filter(hasEditableThickness), [selectedWalls]);
  const isPartialSelection = thicknessWalls.length !== selectedWalls.length;

  const valueMm = useMemo(
    () =>
      getCommonNumber(
        thicknessWalls.map(({ segments }) =>
          NumUtils.metersToMmRounded(segments[0].leftSideWidth + segments[0].rightSideWidth),
        ),
      ),
    [thicknessWalls],
  );

  const onChange = useCallback(
    (newValueMm: number) => {
      if (isPartialSelection || Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);

      fddmSvc.setPreference('highwayArea.segment.width', newValue);
      fddmSvc.updateObstacles(
        selectedWalls
          .filter((wall) => wall.appearance === 'WALL')
          .map((wall) => ({
            ...wall,
            segments: minItemsArrayUtils.mapAtLeast1(wall.segments, (segment) => ({
              ...segment,
              leftSideWidth: newValue / 2,
              rightSideWidth: newValue / 2,
            })),
          })),
      );
    },
    [isPartialSelection, selectedWalls, fddmSvc],
  );

  if (thicknessWalls.length === 0) return null;

  return (
    <PropertiesLabel i18nkey='interface:properties.highway.width'>
      <DisabledReasonTooltip
        reasonKey={
          isPartialSelection
            ? 'interface:properties.wall.partial_selection.only_multi_segment'
            : undefined
        }
      >
        <NumberInput
          // undefined (not false) keeps NumberInput's own read-only-mode check
          disabled={isPartialSelection ? true : undefined}
          value={valueMm}
          unit='mm'
          prefix={<GapHorizontalIcon />}
          sx={{ width: '100%' }}
          minValue={NumUtils.metersToMmRounded(MIN_WALL_THICKNESS)}
          onChange={onChange}
        />
      </DisabledReasonTooltip>
    </PropertiesLabel>
  );
}

export const Thickness = memo(ThicknessComponent);
